import {
  Box,
  Card,
  Typography,
  Button,
  Stack,
  Link,
  useMediaQuery,
  IconButton,
  Menu,
  MenuItem,
  LinearProgress,
} from '@mui/material';
import React, { useState } from 'react';
import {
  Add,
  CheckCircle,
  MoreHoriz,
  DeleteOutline,
} from '@mui/icons-material';
import theme from '../../../../theme';
import { formatPromptDate } from '../../utils';
import { PromptStatus } from '../../../../contexts/DevSettingsContext';

interface PromptCardProps {
  title: string;
  description: string;
  status: PromptStatus;
  images?: string[];
  caption?: string;
  createdAt?: string;
  onAdd?: () => void;
  onDelete?: () => void;
  onReload?: () => void;
}

function PromptCard({
  title,
  description,
  status,
  images = [],
  caption = '',
  createdAt,
  onAdd,
  onDelete,
  onReload,
}: PromptCardProps) {
  const [showFullCaption, setShowFullCaption] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const maxCaptionLength = 200;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const photoGap = 6;
  const cols = images.length === 4 || isMobile ? 2 : 3;
  const shouldTruncate = caption.length > maxCaptionLength;
  const displayCaption = showFullCaption || !shouldTruncate
      ? caption
      : `${caption.substring(0, maxCaptionLength)}...`;

  const isSubmitting = status === 'submitting';
  const isError = status === 'error';
  const isCompleted = status === 'completed';
  const showAddButton = status === 'todo';

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };
  const handleMenuClose = () => setMenuAnchor(null);
  const handleDeleteClick = () => {
    setMenuAnchor(null);
    onDelete?.();
  };

  return (
    <Card
      sx={{
        borderRadius: '12px',
        padding: 3,
        boxShadow: '0px 0px 4px rgba(0, 0, 0, 0.08)',
      }}
    >
      <Stack spacing={3}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            position: 'relative',
            gap: 1.5,
            flexDirection: { xs: 'column', md: 'row' },
          }}
        >
          <Stack
            sx={{ flexGrow: 1, width: '100%', userSelect: 'none' }}
            spacing={0.5}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: '18px',
                fontWeight: 700,
                lineHeight: 1.25,
                letterSpacing: 0,
              }}
            >
              {title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontSize: '14px',
                lineHeight: 1.4,
              }}
            >
              {description}
            </Typography>
          </Stack>

          {showAddButton && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={onAdd}
              sx={{
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: '160px',
                width: { md: 'auto' },
              }}
            >
              Add
            </Button>
          )}

          {isCompleted && (
            <>
              <IconButton
                aria-label="prompt actions"
                onClick={handleMenuOpen}
                sx={{
                  flexShrink: 0,
                  position: 'absolute',
                  top: '-10px',
                  right: '-8px',
                }}
              >
                <MoreHoriz />
              </IconButton>
              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={handleMenuClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                slotProps={{
                  paper: {
                    elevation: 6,
                    sx: {
                      borderRadius: '12px',
                      py: 0.25,
                    },
                  },
                  list: {
                    sx: { py: 0 },
                  },
                }}
              >
                <MenuItem
                  onClick={handleDeleteClick}
                  sx={{
                    gap: 1.5,
                    px: 3,
                    py: 2,
                    color: '#B3261E',
                    fontFamily: 'Lexend, sans-serif',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: 1.5,
                  }}
                >
                  <DeleteOutline sx={{ fontSize: '24px', opacity: 0.7 }} />
                  Delete Memory
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {isSubmitting && (
          <Box
            sx={{
              py: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1.5,
            }}
          >
            <Box sx={{ width: '60%', maxWidth: 280 }}>
              <LinearProgress
                sx={{
                  height: 6,
                  borderRadius: '10px',
                  backgroundColor: '#e8e1f4',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: '10px',
                    backgroundColor: 'primary.main',
                  },
                }}
              />
            </Box>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: '14px' }}
            >
              Uploading…
            </Typography>
          </Box>
        )}

        {isError && (
          <Box
            sx={{
              py: 3,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              textAlign: 'center',
            }}
          >
            <Box sx={{ mb: 0.5 }}>
              <img
                src="/dog_cat.png"
                alt="Upload failed"
                width={80}
                height={80}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = 'none';
                }}
              />
            </Box>
            <Typography variant="h6" sx={{ fontSize: '18px', fontWeight: 600 }}>
              Oops! Something went wrong
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: 'text.secondary', fontSize: '14px', maxWidth: 360 }}
            >
              Reload the page or email us at support@petcademy.org if the issue
              continues
            </Typography>
            <Link
              component="button"
              onClick={onReload}
              sx={{
                mt: 0.5,
                fontSize: '14px',
                fontWeight: 600,
                color: 'primary.main',
                textDecoration: 'none',
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                p: 0,
                fontFamily: 'inherit',
              }}
            >
              Reload Page
            </Link>
          </Box>
        )}

        {isCompleted && images.length > 0 && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: `${photoGap}px`,
              width: '100%',
              justifyContent: 'center',
            }}
          >
            {images.map((img, idx) => (
              <Box
                key={img}
                sx={{
                  flexGrow: 1,
                  width: `calc((100% - ${photoGap * (cols - 1)}px) / ${cols})`,
                  aspectRatio: '1/1',
                  borderRadius: '2px',
                  overflow: 'hidden',
                  maxWidth: '50vh',
                }}
              >
                <img
                  src={img}
                  alt={`${title} ${idx + 1}`}
                  loading="lazy"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                  }}
                />
              </Box>
            ))}
          </Box>
        )}

        {isCompleted && (caption || createdAt) && (
          <Stack spacing={1.5}>
            {caption && (
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    lineHeight: 1.4,
                    color: 'text.primary',
                  }}
                >
                  {displayCaption}
                </Typography>
                {shouldTruncate && (
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => setShowFullCaption(!showFullCaption)}
                    sx={{
                      fontSize: '14px',
                      lineHeight: 1.4,
                      color: 'text.secondary',
                      textDecoration: 'underline',
                      cursor: 'pointer',
                      border: 'none',
                      background: 'none',
                      p: 0,
                      fontFamily: 'inherit',
                    }}
                  >
                    {showFullCaption ? 'Show less' : 'Read more'}
                  </Link>
                )}
              </Box>
            )}

            {createdAt && (
              <Stack
                direction="row"
                spacing={0.75}
                alignItems="center"
                sx={{ height: '20px' }}
              >
                <CheckCircle sx={{ fontSize: '20px', color: 'primary.main' }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    lineHeight: 1.4,
                    color: 'text.secondary',
                  }}
                >
                  {formatPromptDate(createdAt)}
                </Typography>
              </Stack>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

export default PromptCard;
